import { useAccount, useMsal } from "@azure/msal-react";
import { type UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChatVectorIndexOption, DataSourceWithIndexDescription, RawDatasource } from "src/db";
import { type ConfigurationResponse, fetchConfiguration } from "./useConfiguration";

function flattenDatasources(raw: RawDatasource[]): ChatVectorIndexOption[] {
    return raw.flatMap((ds) => {
        if (ds.index.length === 0) {
            return [
                {
                    label: ds.label,
                    id: ds.id,
                    datasource_id: ds.id,
                    datasource_label: ds.label,
                    index_id: "",
                    enable_chat_with_document: ds.enable_chat_with_document,
                    index_description: [],
                },
            ];
        }
        return ds.index.map((idx) => ({
            label: idx.label,
            id: `${ds.id}/${idx.index_id}`,
            datasource_id: ds.id,
            datasource_label: ds.label,
            index_id: idx.index_id,
            enable_chat_with_document: ds.enable_chat_with_document,
            index_description: idx.index_description,
        }));
    });
}

// Shares the ["configuration"] query cache with useOrganizations/useDatasourceOverview
// so all three hooks resolve from a single /v1/system/configuration request instead of one each.
export const useDatasources = (): UseQueryResult<ChatVectorIndexOption[], Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<ConfigurationResponse, Error, ChatVectorIndexOption[]>({
        queryKey: ["configuration"],
        queryFn: () => fetchConfiguration(instance),
        select: (config) => {
            const data = flattenDatasources(config.datasources);
            localStorage.setItem("datasourceDetails", JSON.stringify(data));
            return data;
        },
        enabled: isAuthenticated && inProgress === "none",
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};

export const useDatasourceOverview = (): UseQueryResult<RawDatasource[], Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<ConfigurationResponse, Error, RawDatasource[]>({
        queryKey: ["configuration"],
        queryFn: () => fetchConfiguration(instance),
        select: (config) => config.datasources,
        enabled: isAuthenticated && inProgress === "none",
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};

export const useColumns = (
    index: string,
): UseQueryResult<DataSourceWithIndexDescription, Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;
    const queryClient = useQueryClient();

    return useQuery<DataSourceWithIndexDescription, Error>({
        queryKey: ["datasources", index],
        queryFn: async () => {
            // Goes through the shared ["configuration"] query cache (ensureQueryData resolves
            // instantly from cache if another hook already fetched it) instead of calling the
            // API directly, so this never causes a second /v1/system/configuration request.
            const config = await queryClient.ensureQueryData<ConfigurationResponse>({
                queryKey: ["configuration"],
                queryFn: () => fetchConfiguration(instance),
                staleTime: Number.POSITIVE_INFINITY,
            });
            const sources = flattenDatasources(config.datasources);
            const source = sources?.find((o) => o.id === index);

            return {
                index_description: source.index_description,
                namespace: source.index_id,
            };
        },
        enabled: isAuthenticated && inProgress === "none",
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};
